// Page4_RecommendedCoupons.jsx
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Info, Percent, DollarSign, Star, ChevronLeft, Check, X, RefreshCw } from 'react-feather';
import ConfirmPublishPopupIcon from '../../assets/images/ConfirmPublishPopupIcon.svg';
import { actions as aiActions } from 'coupon-common/src/modules/aiContent';
import { actions as couponImageActions } from 'coupon-common/src/modules/couponImages';
import { dispatchAsAsync } from 'coupon-common/src/utils';
import { toast } from 'react-toastify';
import { allCouponPrompt } from '../../coupon-ai/prompts';
import styles from './Page4_RecommendedCoupons.module.scss';

const TOTAL_STEPS = 5;
const CURRENT_STEP = 4;
const RED = '#c0392b';
const HERO_SLOT_ID = 1;
const HERO_SEQUENCE = 1;
const MAX_FILE_SIZE_MB = 2;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// Survives Page4 unmount/remount (e.g. stepping back and forth in the wizard)
// within the same session, but resets on a real page load. Keyed so a change
// to the underlying inputs (item, goal, speed, scope, etc.) still triggers a
// fresh generation instead of showing stale copy.
let p4GenerationCache = null;

const LOADING_LABELS = [
  'Understanding your desired goals...',
  'Analyzing effective solutions...',
  'Checking current market trends...',
  'Finalizing your recommended deals!',
];

// ─── Responsive hook ──────────────────────────────────────────────────────────
const useWindowWidth = () => {
  const [width, setWidth] = React.useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  React.useEffect(() => {
    const handle = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  return width;
};

const applyTemplate = (template, vars) =>
  String(template).replace(/\{\{(\w+)\}\}/g, (_, key) =>
    vars[key] !== undefined && vars[key] !== null ? String(vars[key]) : '',
  );

const currencyFmt = (n, currency = 'USD') =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n);

const TITLE_ANGLE_POOL = [
  'Lead with the specific savings amount or percentage in a bold, clear way',
  'Emphasize the tangible benefit the customer receives',
  'Focus on what the customer gets, not what they pay',
  'Create time-sensitivity without using the word "limited" or "now"',
  'Convey scarcity through implied popularity or high demand',
  'Use exclusivity as the hook without explicitly stating "exclusive"',
  'Name the specific dish or item with an appetizing descriptor',
  'Describe the sensory experience (taste, texture, aroma)',
  'Paint a picture of the consumption moment (where, when, with whom)',
  'Start with an imperative verb that creates immediate desire',
  'Frame as a discovery or invitation to try something new',
  'Use second-person "your" to create personal connection',
  'Connect to a specific time of day or day of week',
  'Reference a seasonal theme or weather condition',
  'Tie to a social occasion (date night, family dinner, solo treat)',
  'Evoke nostalgia or comfort without using the word "comfort"',
  'Create anticipation for a rewarding experience',
  'Suggest indulgence or treating oneself',
  'Include a unique ingredient or preparation method',
  'Reference the origin or style of cuisine',
  'Mention a specific portion size or combo structure',
  'Address a specific craving or desire',
  'Solve an unstated problem (quick lunch, late night hunger)',
  'Position as the perfect choice for a common situation',
  'Use unexpected word pairing or contrast',
  'Create rhythm through alliteration (without forcing it)',
  'Employ understatement or subtle humor',

  // Social proof/community angles
  'Reference local popularity or neighborhood favourite status',
  'Suggest joining others who already enjoy this',
  'Imply insider knowledge or being "in the know"',
  'Juxtapose premium quality with accessible pricing',
  'Contrast everyday routine with special experience',
  'Compare to a more expensive alternative',
  'Hint at something special without fully revealing it',
  'Pose a subtle question the customer wants answered',
  'Create curiosity through an unexpected combination',
  'Start with an action verb (Skip, Grab, Unlock, Discover)',
  'Use a time or number reference (3-Hour, Sunset, Midweek)',
  'Begin with a single powerful word followed by a descriptor',
  'Create a location/setting marker (Corner Cafe, After-Hours)',
  'Use possessive structure (Your Daily..., Their Favourite...)',
];

const GENERIC_TITLE_STARTERS = [
  'Deal', 'Offer', 'Special', 'Save', 'Discount', 'Get', 'Exclusive',
  'Limited', 'Flash', 'Now', 'Today', 'Fresh', 'New', 'Hot', 'Best',
];

function cyrb128(str) {
  let h1 = 1779033703, h2 = 3144134277, h3 = 1013904242, h4 = 2773480762;
  for (let i = 0, k; i < str.length; i++) {
    k = str.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  return [(h1 ^ h2 ^ h3 ^ h4) >>> 0, (h2 ^ h1) >>> 0, (h3 ^ h1) >>> 0, (h4 ^ h1) >>> 0];
}

function mulberry32(a) {
  return function () {
    let t = (a += 0x6D2B79F5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function seededShuffle(arr, seedStr) {
  const a = Array.isArray(arr) ? [...arr] : [];
  const seed = cyrb128(String(seedStr || 'seed'))[0];
  const rand = mulberry32(seed);
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function pickDistinctTitleAngles(seedStr, count = 3) {
  const shuffled = seededShuffle(TITLE_ANGLE_POOL, seedStr);
  const picked = shuffled.slice(0, Math.min(count, TITLE_ANGLE_POOL.length));
  while (picked.length < count) picked.push(shuffled[picked.length % shuffled.length]);
  return picked;
}

const CHART_LOGIC = {
  'New Customers': {
    Steady: {
      'Individual Item': [
        { type: 'points', pointsMultiplier: 10, minSpend: 0, badge: 'Strategic', expiry: 30, limit: 3, redemptionType: 'In-App', pointsAllowed: true, reservationHours: 24 },
        { type: 'amount', pctOfPrice: 15, minSpend: 0, badge: 'High Convert', pointsAllowed: true, expiry: 30, limit: 3, reservationHours: 24 },
        { type: 'amount', pctOfPrice: 30, minSpend: 0, badge: 'Aggressive', pointsAllowed: true, expiry: 30, limit: 3, reservationHours: 24 },
      ],
      'Product Line': [
        { type: 'percent', val: 10, minSpend: 50, badge: 'Strategic', pointsAllowed: false, expiry: 30, limit: 3, reservationHours: 24 },
        { type: 'percent', val: 15, minSpend: 0, badge: 'Balanced', redemptionType: 'Auto', pointsAllowed: false, expiry: 30, limit: 3, reservationHours: 24 },
        { type: 'points', val: 500, minSpend: 50, badge: 'Loyalty', pointsAllowed: false, expiry: 30, limit: 3, reservationHours: 24 },
      ],
      'Storewide Promotion': [
        { type: 'amount', pctOfPrice: 10, minSpend: 0, badge: 'Strategic', pointsAllowed: true, expiry: 14, limit: 2, reservationHours: 24 },
        { type: 'percent', val: 10, minSpend: 0, badge: 'Balanced', pointsAllowed: false, expiry: 14, limit: 2, reservationHours: 24 },
        { type: 'points', val: 500, minSpend: 50, badge: 'Loyalty', pointsAllowed: false, expiry: 14, limit: 2, reservationHours: 24 },
      ],
    },
    Quick: {
      'Individual Item': [
        { type: 'points', pointsMultiplier: 20, minSpend: 0, badge: 'Strategic', expiry: 14, limit: 2, reservationHours: 24, redemptionType: 'In-App', pointsAllowed: true },
        { type: 'amount', pctOfPrice: 20, minSpend: 0, badge: 'Flash Deal', pointsAllowed: true, expiry: 14, limit: 2, reservationHours: 24 },
        { type: 'amount', pctOfPrice: 30, badge: 'Aggressive', pointsAllowed: true, expiry: 14, limit: 2, reservationHours: 24 },
      ],
      'Product Line': [
        { type: 'percent', val: 20, minSpend: 50, badge: 'Strategic', pointsAllowed: false, expiry: 14, limit: 2, reservationHours: 24 },
        { type: 'percent', val: 30, minSpend: 0, badge: 'Volume', redemptionType: 'Auto', pointsAllowed: false, expiry: 14, limit: 2, reservationHours: 24 },
        { type: 'points', val: 500, minSpend: 25, badge: 'Loyalty', pointsAllowed: false, redemptionType: 'In-App', expiry: 14, limit: 2, reservationHours: 24 },
      ],
      'Storewide Promotion': [
        { type: 'percent', val: 20, minSpend: 50, badge: 'Strategic', pointsAllowed: false, expiry: 14, limit: 2, reservationHours: 24 },
        { type: 'percent', val: 30, minSpend: 0, badge: 'Volume', redemptionType: 'Auto', pointsAllowed: false, expiry: 14, limit: 2, reservationHours: 24 },
        { type: 'points', val: 500, minSpend: 25, badge: 'Loyalty', pointsAllowed: false, redemptionType: 'In-App', expiry: 14, limit: 2, reservationHours: 24 },
      ],
    },
    Max: {
      'Individual Item': [
        { type: 'points', pointsMultiplier: 35, minSpend: 0, badge: 'Clearance', redemptionType: 'In-App', pointsAllowed: true, totalAmount: 100, limit: 2, reservationHours: 24 },
        { type: 'amount', pctOfPrice: 35, minSpend: 0, badge: 'Deep Discount', pointsAllowed: true, totalAmount: 100, limit: 2, reservationHours: 24 },
        { type: 'amount', pctOfPrice: 50, minSpend: 0, badge: 'VIP Only', exclusivity: 'premium', pointsAllowed: false, totalAmount: 100, limit: 2, reservationHours: 24 },
      ],
      'Product Line': [
        { type: 'percent', val: 35, minSpend: 0, badge: 'Flash', pointsAllowed: false, totalAmount: 100, limit: 2, reservationHours: 24 },
        { type: 'percent', val: 50, minSpend: 50, badge: 'Volume', totalAmount: 100, limit: 2, reservationHours: 24, redemptionType: 'Auto', pointsAllowed: false },
        { type: 'points', val: 500, minSpend: 15, badge: 'Points', pointsAllowed: false, redemptionType: 'In-App', totalAmount: 100, limit: 2, reservationHours: 24 },
      ],
      'Storewide Promotion': [
        { type: 'percent', val: 35, minSpend: 0, badge: 'Flash', pointsAllowed: false, totalAmount: 100, limit: 2, reservationHours: 24 },
        { type: 'percent', val: 50, minSpend: 50, badge: 'Volume', totalAmount: 100, limit: 2, reservationHours: 24, redemptionType: 'Auto', pointsAllowed: false },
        { type: 'points', val: 500, minSpend: 15, badge: 'Points', pointsAllowed: false, redemptionType: 'In-App', totalAmount: 100, limit: 2, reservationHours: 24 },
      ],
    },
  },
  'Sell More': {
    Steady: { 'Individual Item': [{ type: 'points', pointsMultiplier: 20, minSpend: 0, badge: 'Standard', totalAmount: 500, limit: 'Unlimited', redemptionType: 'In-App', pointsAllowed: true, reservationHours: 12 }, { type: 'amount', pctOfPrice: 25, minSpend: 0, badge: 'Upsell', pointsAllowed: true, totalAmount: 500, limit: 'Unlimited', reservationHours: 12 }, { type: 'percent', val: 20, minSpend: 0, badge: 'Volume', pointsAllowed: true, totalAmount: 500, limit: 'Unlimited', reservationHours: 12 }] },
    Quick: { 'Individual Item': [{ type: 'points', pointsMultiplier: 10, minSpend: 0, badge: 'Standard', totalAmount: 250, reservationHours: 12, redemptionType: 'In-App', pointsAllowed: true }, { type: 'amount', pctOfPrice: 35, minSpend: 0, badge: 'Flash', pointsAllowed: true, limit: 'Unlimited', totalAmount: 250, reservationHours: 12 }, { type: 'amount', pctOfPrice: 30, minSpend: 0, badge: 'Aggressive', pointsAllowed: true, totalAmount: 250, reservationHours: 12, limit: 'Unlimited' }] },
    Max: { 'Individual Item': [{ type: 'points', pointsMultiplier: 40, minSpend: 0, badge: 'Clearance', totalAmount: 100, limit: 'Unlimited', reservationHours: 12 }, { type: 'amount', pctOfPrice: 50, minSpend: 0, badge: 'Deep Discount', exclusivity: 'premium', pointsAllowed: false, totalAmount: 100, limit: 'Unlimited', reservationHours: 12 }, { type: 'amount', pctOfPrice: 40, minSpend: 0, badge: 'VIP', pointsAllowed: false, totalAmount: 100, limit: 'Unlimited', reservationHours: 12 }] },
  },
  'Repeat Business': {
    Steady: {
      'Individual Item': [{ type: 'points', pointsMultiplier: 20, minSpend: 0, badge: 'Loyalty', expiry: 30, limit: 5, redemptionType: 'In-App', pointsAllowed: true, reservationHours: 24, exclusivity: 'followers' }, { type: 'amount', pctOfPrice: 25, minSpend: 0, badge: 'Discount', pointsAllowed: true, reservationHours: 24, exclusivity: 'followers' }, { type: 'amount', pctOfPrice: 20, minSpend: 0, badge: 'Savings', pointsAllowed: true, reservationHours: 24, exclusivity: 'followers' }],
      'Product Line': [{ type: 'points', val: 500, minSpend: 20, badge: 'Standard', pointsAllowed: true, exclusivity: 'followers' }, { type: 'amount', val: 10, minSpend: 40, badge: 'Loyalty', expiry: 30, limit: 'Unlimited', redemptionType: 'auto', pointsAllowed: false, exclusivity: 'followers' }, { type: 'percent', val: 20, badge: 'Volume', pointsAllowed: false, limit: 'Unlimited', redemptionType: 'auto', exclusivity: 'followers' }],
      'Storewide Promotion': [{ type: 'points', val: 500, minSpend: 20, badge: 'Standard', pointsAllowed: true, exclusivity: 'followers' }, { type: 'amount', val: 10, minSpend: 40, badge: 'Loyalty', expiry: 30, limit: 'Unlimited', redemptionType: 'auto', pointsAllowed: false, exclusivity: 'followers' }, { type: 'percent', val: 20, badge: 'Volume', pointsAllowed: false, limit: 'Unlimited', redemptionType: 'auto', exclusivity: 'followers' }],
    },
    Quick: {
      'Individual Item': [{ type: 'points', pointsMultiplier: 30, badge: 'Loyalty', redemptionType: 'In-App', pointsAllowed: true }, { type: 'amount', pctOfPrice: 30, badge: 'Upsell', pointsAllowed: true }, { type: 'amount', pctOfPrice: 30, minSpend: 0, badge: 'Direct', pointsAllowed: false }],
      'Product Line': [{ type: 'points', val: 1000, minSpend: 30, badge: 'Loyalty', redemptionType: 'In-App', reservationHours: 24 }, { type: 'amount', val: 10, minSpend: 25, badge: 'Standard', pointsAllowed: false, redemptionType: 'Auto', reservationHours: 24 }, { type: 'percent', val: 30, badge: 'Volume', pointsAllowed: false, redemptionType: 'Auto', reservationHours: 24 }],
      'Storewide Promotion': [{ type: 'points', val: 1000, minSpend: 30, badge: 'Loyalty', redemptionType: 'In-App', reservationHours: 24 }, { type: 'amount', val: 10, minSpend: 25, badge: 'Standard', pointsAllowed: false, redemptionType: 'Auto', reservationHours: 24 }, { type: 'percent', val: 30, badge: 'Volume', pointsAllowed: false, redemptionType: 'Auto', reservationHours: 24 }],
    },
    Max: {
      'Individual Item': [{ type: 'points', pointsMultiplier: 50, badge: 'Loyalty', redemptionType: 'In-App' }, { type: 'amount', pctOfPrice: 50, minSpend: 0, badge: 'VIP', exclusivity: 'premium', pointsAllowed: false, reservationHours: 24 }, { type: 'amount', pctOfPrice: 35, badge: 'Upsell', pointsAllowed: false, reservationHours: 24, redemptionType: 'Auto' }],
      'Product Line': [{ type: 'points', val: 1500, minSpend: 30, badge: 'Loyalty', expiry: 14, limit: 2, redemptionType: 'In-App', pointsAllowed: false, reservationHours: 24 }, { type: 'amount', val: 15, minSpend: 25, badge: 'Standard', pointsAllowed: false, exclusivity: 'premium', redemptionType: 'Auto', reservationHours: 24 }, { type: 'percent', val: 40, redemptionType: 'Auto', reservationHours: 24, badge: 'Volume', pointsAllowed: false }],
      'Storewide Promotion': [{ type: 'points', val: 1500, minSpend: 30, badge: 'Loyalty', expiry: 14, limit: 2, redemptionType: 'In-App', pointsAllowed: false, reservationHours: 24 }, { type: 'amount', val: 15, minSpend: 25, badge: 'Standard', pointsAllowed: false, exclusivity: 'premium', redemptionType: 'Auto', reservationHours: 24 }, { type: 'percent', val: 40, redemptionType: 'Auto', reservationHours: 24, badge: 'Volume', pointsAllowed: false }],
    },
  },
  'Slow Periods': {
    Steady: {
      'Individual Item': [{ type: 'points', pointsMultiplier: 20, badge: 'Low Barrier', expiry: 90, limit: 'Unlimited', reservationHours: 24, redemptionType: 'In-App', pointsAllowed: true }, { type: 'amount', pctOfPrice: 20, minSpend: 0, badge: 'Standard', expiry: 90, limit: 'Unlimited', reservationHours: 24, pointsAllowed: true }, { type: 'amount', pctOfPrice: 15, minSpend: 0, badge: 'Premium', expiry: 60, limit: 'Unlimited', reservationHours: 24, exclusivity: 'premium', pointsAllowed: true }],
      'Product Line': [{ type: 'points', val: 800, minSpend: 40, badge: 'Low Barrier', expiry: 90, limit: 'Unlimited', reservationHours: 24, redemptionType: 'In-App', pointsAllowed: false }, { type: 'percent', val: 20, minSpend: 60, badge: 'Premium', expiry: 90, limit: 'Unlimited', reservationHours: 24, exclusivity: 'premium', pointsAllowed: false }, { type: 'amount', val: 10, minSpend: 50, badge: 'Standard', expiry: 90, limit: 'Unlimited', reservationHours: 24, pointsAllowed: false }],
      'Storewide Promotion': [{ type: 'points', val: 800, minSpend: 40, badge: 'Low Barrier', expiry: 90, limit: 'Unlimited', reservationHours: 24, redemptionType: 'In-App', pointsAllowed: false }, { type: 'percent', val: 20, minSpend: 60, badge: 'Premium', expiry: 90, limit: 'Unlimited', reservationHours: 24, exclusivity: 'premium', pointsAllowed: false }, { type: 'amount', val: 10, minSpend: 50, badge: 'Standard', expiry: 90, limit: 'Unlimited', reservationHours: 24, pointsAllowed: false }],
    },
    Quick: {
      'Individual Item': [{ type: 'points', pointsMultiplier: 30, badge: 'Low Barrier', expiry: 90, limit: 5, reservationHours: 24, redemptionType: 'In-App' }, { type: 'amount', pctOfPrice: 30, minSpend: 0, badge: 'Standard', expiry: 90, limit: 5, reservationHours: 24, pointsAllowed: true }, { type: 'percent', val: 30, minSpend: 0, badge: 'Premium', expiry: 90, limit: 5, reservationHours: 24, pointsAllowed: true }],
      'Product Line': [{ type: 'points', val: 800, minSpend: 25, badge: 'Low Barrier', expiry: 90, limit: 5, reservationHours: 24, redemptionType: 'In-App', pointsAllowed: false }, { type: 'percent', val: 30, minSpend: 25, badge: 'Standard', expiry: 90, limit: 5, reservationHours: 24, pointsAllowed: true }, { type: 'percent', val: 30, minSpend: 50, badge: 'Premium', expiry: 90, limit: 5, reservationHours: 24, pointsAllowed: false }],
      'Storewide Promotion': [{ type: 'points', val: 800, minSpend: 25, badge: 'Low Barrier', expiry: 90, limit: 5, reservationHours: 24, redemptionType: 'In-App', pointsAllowed: false }, { type: 'percent', val: 30, minSpend: 25, badge: 'Standard', expiry: 90, limit: 5, reservationHours: 24, pointsAllowed: true }, { type: 'percent', val: 30, minSpend: 50, badge: 'Premium', expiry: 90, limit: 5, reservationHours: 24, pointsAllowed: false }],
    },
    Max: {
      'Individual Item': [{ type: 'points', pointsMultiplier: 40, badge: 'Easy Points', expiry: 90, limit: 'Unlimited', reservationHours: 24, redemptionType: 'In-App' }, { type: 'amount', pctOfPrice: 40, badge: 'Doorbuster', expiry: 90, limit: 'Unlimited', reservationHours: 24, pointsAllowed: true, exclusivity: 'premium' }, { type: 'percent', val: 35, badge: 'Premium Only', expiry: 90, limit: 'Unlimited', reservationHours: 24, pointsAllowed: false }],
      'Product Line': [{ type: 'points', val: 800, minSpend: 15, badge: 'Low Barrier', expiry: 90, limit: 'Unlimited', reservationHours: 24, redemptionType: 'In-App', pointsAllowed: true }, { type: 'percent', val: 40, badge: 'Category Deal', expiry: 90, limit: 'Unlimited', reservationHours: 24, pointsAllowed: true }, { type: 'percent', val: 35, minSpend: 0, badge: 'Exclusive', expiry: 90, limit: 'Unlimited', reservationHours: 24, pointsAllowed: false }],
      'Storewide Promotion': [{ type: 'points', val: 800, minSpend: 15, badge: 'Low Barrier', expiry: 90, limit: 'Unlimited', reservationHours: 24, redemptionType: 'In-App', pointsAllowed: true }, { type: 'percent', val: 40, badge: 'Category Deal', expiry: 90, limit: 'Unlimited', reservationHours: 24, pointsAllowed: true, exclusivity: 'premium' }, { type: 'percent', val: 35, minSpend: 0, badge: 'Exclusive', expiry: 90, limit: 'Unlimited', reservationHours: 24, pointsAllowed: false }],
    },
  },
  'Boost Promotion': {
    Steady: {
      'Individual Item': [{ type: 'points', pointsMultiplier: 10, badge: 'Points', expiry: 14, limit: 'Unlimited', redemptionType: 'In-App', reservationHours: 24 }, { type: 'percent', val: 15, minSpend: 0, badge: 'Discount', expiry: 14, limit: 'Unlimited', pointsAllowed: true, reservationHours: 24 }, { type: 'amount', pctOfPrice: 10, minSpend: 0, badge: 'Flash', expiry: 14, limit: 'Unlimited', pointsAllowed: false, reservationHours: 24 }],
      'Product Line': [{ type: 'points', val: 1000, minSpend: 50, badge: 'Points', expiry: 14, limit: 'Unlimited', redemptionType: 'In-App', reservationHours: 24 }, { type: 'percent', val: 15, badge: 'Standard', expiry: 14, limit: 'Unlimited', pointsAllowed: false, reservationHours: 24 }, { type: 'amount', val: 10, minSpend: 50, badge: 'Volume', expiry: 14, limit: 'Unlimited', pointsAllowed: false, reservationHours: 24 }],
      'Storewide Promotion': [{ type: 'points', val: 1000, minSpend: 50, badge: 'Points', expiry: 14, limit: 'Unlimited', redemptionType: 'In-App', reservationHours: 24 }, { type: 'percent', val: 15, badge: 'Standard', expiry: 14, limit: 'Unlimited', pointsAllowed: false, reservationHours: 24 }, { type: 'amount', val: 10, minSpend: 50, badge: 'Volume', expiry: 14, limit: 'Unlimited', pointsAllowed: false, reservationHours: 24 }],
    },
    Quick: {
      'Individual Item': [{ type: 'amount', pctOfPrice: 20, minSpend: 0, badge: 'Flash', expiry: 7, limit: 3, pointsAllowed: true }, { type: 'percent', val: 15, minSpend: 0, badge: 'Deep Discount', expiry: 14, pointsAllowed: false }, { type: 'points', pointsMultiplier: 15, minSpend: 0, badge: 'Points', expiry: 14, redemptionType: 'In-App' }],
      'Product Line': [{ type: 'percent', val: 15, minSpend: 25, badge: 'Standard', expiry: 14, pointsAllowed: false }, { type: 'percent', val: 20, badge: 'Volume', expiry: 14, pointsAllowed: false }, { type: 'points', val: 1000, minSpend: 40, badge: 'Points', expiry: 14, redemptionType: 'In-App', pointsAllowed: true }],
      'Storewide Promotion': [{ type: 'percent', val: 15, minSpend: 25, badge: 'Standard', expiry: 14, limit: 3, pointsAllowed: false }, { type: 'percent', val: 20, badge: 'Volume', expiry: 14, pointsAllowed: false }, { type: 'points', val: 1000, minSpend: 40, badge: 'Points', expiry: 14, redemptionType: 'In-App', pointsAllowed: true }],
    },
    Max: {
      'Individual Item': [{ type: 'amount', pctOfPrice: 30, minSpend: 0, badge: 'Doorbuster', expiry: 14, pointsAllowed: true, exclusivity: 'premium' }, { type: 'percent', val: 20, minSpend: 15, badge: 'Premium', expiry: 14, pointsAllowed: false }, { type: 'points', pointsMultiplier: 20, minSpend: 0, badge: 'High Points', expiry: 14, redemptionType: 'In-App', pointsAllowed: false }],
      'Product Line': [{ type: 'percent', val: 20, minSpend: 15, badge: 'Flash', expiry: 14, pointsAllowed: false }, { type: 'percent', val: 30, badge: 'Volume', expiry: 14, pointsAllowed: false }, { type: 'points', val: 1000, minSpend: 30, badge: 'High Points', expiry: 14, redemptionType: 'In-App', pointsAllowed: false }],
      'Storewide Promotion': [{ type: 'percent', val: 20, minSpend: 15, badge: 'Flash', expiry: 14, pointsAllowed: false }, { type: 'percent', val: 30, badge: 'Volume', expiry: 14, pointsAllowed: false }, { type: 'points', val: 1000, minSpend: 30, badge: 'High Points', expiry: 14, redemptionType: 'In-App', pointsAllowed: false }],
    },
  },
};

const normalizeGoal = g => { if (!g) return 'New Customers'; const s = String(g).toLowerCase(); if (s.includes('new') || s.includes('attract')) return 'New Customers'; if (s.includes('sell')) return 'Sell More'; if (s.includes('repeat')) return 'Repeat Business'; if (s.includes('slow') || s.includes('off-peak')) return 'Slow Periods'; return 'Boost Promotion'; };
const normalizeSpeed = v => { if (!v) return 'Steady'; const s = String(v).toLowerCase().trim(); if (['steady', 'steady_growth', 'balanced', '1'].includes(s)) return 'Steady'; if (['quick', 'quick_boost', 'accelerated', '2'].includes(s)) return 'Quick'; if (['max', 'maximum_impact', 'aggressive', '3'].includes(s)) return 'Max'; return 'Steady'; };
const normalizeScope = sc => { if (!sc) return 'Individual Item'; const s = String(sc).toLowerCase(); if (s.includes('individual')) return 'Individual Item'; if (s.includes('line') || s.includes('category')) return 'Product Line'; if (s.includes('store') || s.includes('storewide') || s === 'all') return 'Storewide Promotion'; return 'Individual Item'; };
const getBusinessName = (biz = {}) => { const val = biz.display_name || biz.legal_name || biz.business_name || biz.name || 'Your Business'; if (typeof val === 'string') return val; return val?.display || val?.text || val?.value || 'Your Business'; };
const getCity = (biz = {}) => { const c = biz.city || biz.location_city || (biz.address && biz.address.city); if (!c) return 'Your City'; if (typeof c === 'string') return c; return c.name || c.city || c.label || 'Your City'; };
const getAddress = (biz = {}) => { const addr = biz.address || biz.street_address || biz.streetAddress || biz.business_address; if (!addr) return ''; if (typeof addr === 'string') return addr; return [addr.street || addr.line1 || '', addr.city || '', addr.province || addr.state || ''].filter(Boolean).join(', '); };

function generateRecommendations(goal, speed, scope, itemPrice = 0, exclusivity = '', userType = 'basic') {
  const gNorm = normalizeGoal(goal), sNorm = normalizeSpeed(speed), scNorm = normalizeScope(scope);
  const goalGroup = CHART_LOGIC[gNorm] || CHART_LOGIC['New Customers'];
  const speedGroup = goalGroup[sNorm] || goalGroup['Steady'];
  const rulesArray = speedGroup[scNorm] || speedGroup['Individual Item'];
  const build = (rule, index) => {
    let type = rule.type, value = 0, amount = 0, points = 0;
    if (type === 'amount' && rule.pctOfPrice) { amount = Math.ceil(itemPrice * (rule.pctOfPrice / 100)); if (amount < 1) amount = 1; if (itemPrice > 0 && amount > itemPrice) { amount = Math.floor(itemPrice * 0.95); if (amount < 1) amount = 1; } }
    else if (type === 'amount') { amount = Number(rule.val ?? rule.amount ?? 5) || 5; }
    else if (type === 'percent') { value = rule.val; }
    else if (type === 'points') { if (rule.pointsMultiplier) { points = Math.ceil(itemPrice * rule.pointsMultiplier); points = Math.round(points / 10) * 10; if (points < 10) points = 10; } else { points = rule.val || 0; } }
    const hasExpiry = rule.expiry !== undefined && rule.expiry !== null;
    const hasTotal = rule.totalAmount !== undefined && rule.totalAmount !== null;
    const expiryType = hasTotal ? 'amount' : hasExpiry ? 'date' : 'date';
    return { type, value, amount, points, minSpend: rule.minSpend || 0, badge: rule.badge || (index === 0 ? 'Standard' : index === 1 ? 'Aggressive' : 'Points'), meta: { expiryType, expiryDays: expiryType === 'date' ? Number(rule.expiry) || 30 : undefined, totalCoupons: expiryType === 'amount' ? Number(rule.totalAmount) || 0 : null, perUserLimit: rule.limit || (userType === 'basic' ? 'Unlimited' : 1), redemptionType: rule.redemptionType || 'Auto', exclusivity: rule.exclusivity || exclusivity, redeemableWithPoints: rule.pointsAllowed !== false, reservationHours: rule.reservationHours ?? rule.reservation ?? null } };
  };
  return rulesArray.map((rule, index) => build(rule, index));
}

function buildCopy(reward, ctx) {
  const { itemName = 'this item', scope, goal, exclusivity } = ctx;
  const excl = exclusivity === 'premium' ? ' • Premium Members' : exclusivity === 'followers' ? ' • Followers' : '';
  const scopeLabel = scope === 'Product Line' ? 'on this line' : scope === 'Storewide Promotion' ? 'storewide' : `on ${itemName}`;
  const minSpendText = reward.minSpend > 0 ? ` when you spend ${currencyFmt(reward.minSpend)}` : '';
  const reservationText = reward.meta.reservationHours ? ` (Must reserve within ${reward.meta.reservationHours}h)` : '';
  switch (reward.type) {
    case 'percent': return { title: `${reward.value}% off ${scope === 'Individual Item' ? itemName : scopeLabel}${excl}`, desc: `Get ${reward.value}% discount${minSpendText}. Optimized for ${goal}.${reservationText}`, terms: 'Standard terms apply.' };
    case 'amount': return { title: `Save ${currencyFmt(reward.amount)} on ${scope === 'Individual Item' ? itemName : scopeLabel}${excl}`, desc: `Instant ${currencyFmt(reward.amount)} savings${minSpendText}.${reservationText}`, terms: 'Standard terms apply.' };
    case 'points': return { title: `${reward.points?.toLocaleString()} points back${excl}`, desc: `Earn ${reward.points?.toLocaleString()} points${minSpendText}.${reservationText}`, terms: 'Standard terms apply.' };
    default: return { title: 'Recommended Coupon', desc: 'Auto-generated for you.', terms: 'Standard terms apply.' };
  }
}

const cleanLineValue = s => String(s || '').replace(/^\s*["'`]+|["'`]+\s*$/g, '').trim();
function parseAllCouponText(text) {
  const raw = String(text || '').trim();
  const getSection = label => { const re = new RegExp(String.raw`(?:^|\n)\s*${label}\s*:\s*([\s\S]*?)(?=\n\s*(Title|Description|Terms of Service)\s*:|$)`, 'i'); const m = raw.match(re); return m ? m[1].trim() : ''; };
  let title = getSection('Title'), description = getSection('Description'), terms = getSection('Terms of Service');
  if (!title) { const m = raw.match(/^\s*Title\s*[-:]\s*(.+)$/im); if (m) title = m[1].trim(); }
  if (!description) { const m = raw.match(/^\s*Description\s*[-:]\s*([\s\S]+?)$/im); if (m) description = m[1].trim(); }
  if (!terms) { const m = raw.match(/^\s*Terms(?:\s+of\s+Service)?\s*[-:]\s*([\s\S]+)$/im); if (m) terms = m[1].trim(); }
  title = cleanLineValue(title).replace(/\s+/g, ' ');
  description = cleanLineValue(description).replace(/\s+/g, ' ');
  terms = cleanLineValue(terms).replace(/\s+/g, ' ');
  if (title.length > 36) title = title.slice(0, 36).trim();
  if (description.length > 1000) description = description.slice(0, 1000).trim();
  if (terms.length > 580) terms = terms.slice(0, 580).trim();
  return { title, description, terms };
}

function couponTypeForReward(r) { return r.type === 'points' ? 'points' : r.type === 'percent' ? 'percentage' : 'dollar'; }
function discountValueForReward(r) { return r.type === 'points' ? r.points : r.type === 'percent' ? r.value : r.amount; }

// The Points reward type is temporarily disabled — shown but not selectable.
function isPointsCard(card) { return card?.raw?.type === 'points'; }

// ── Loading Labels ────────────────────────────────────────────────────────────
function LoadingLabels() {
  const [visibleIdx, setVisibleIdx] = useState(0);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (visibleIdx >= LOADING_LABELS.length - 1) return;
    const holdTimer = setTimeout(() => {
      setFading(true);
      const fadeTimer = setTimeout(() => {
        setVisibleIdx(prev => prev + 1);
        setFading(false);
      }, 500);
      return () => clearTimeout(fadeTimer);
    }, 2000);
    return () => clearTimeout(holdTimer);
  }, [visibleIdx]);

  return (
    <div style={{ textAlign: 'center', height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
      <span style={{ fontSize: 15, fontWeight: 600, color: RED, opacity: fading ? 0 : 1, transition: 'opacity 0.5s ease', pointerEvents: 'none', userSelect: 'none' }}>
        {LOADING_LABELS[visibleIdx]}
      </span>
    </div>
  );
}

// ── Phone Mockup ──────────────────────────────────────────────────────────────
function PhoneMockup({ card, scale, isSelected, loading, heroUrl, images = [], businessName, businessLogo, phoneWidth = 300 }) {
  const isCenter = scale >= 0.98;
  const isDisabled = !loading && isPointsCard(card);
  const discountDisplay = () => {
    if (!card) return null;
    const r = card.raw;
    if (r.type === 'percent') return <><span style={{ fontSize: 26, fontWeight: 900, color: RED }}>Save {r.value}%</span><span style={{ fontSize: 13, fontWeight: 700, color: RED }}> OFF</span></>;
    if (r.type === 'amount') return <span style={{ fontSize: 26, fontWeight: 900, color: RED }}>Save {currencyFmt(r.amount)}</span>;
    if (r.type === 'points') return <span style={{ fontSize: 22, fontWeight: 900, color: RED }}>{r.points?.toLocaleString()} pts</span>;
    return null;
  };

  const slides = images && images.length > 0 ? images : (heroUrl ? [heroUrl] : []);
  const slideCount = slides.length;
  const [slideIdx, setSlideIdx] = useState(0);
  const touchStartXRef = useRef(null);

  useEffect(() => { if (slideIdx > slideCount - 1) setSlideIdx(0); }, [slideCount, slideIdx]);

  const goTo = (i) => { if (slideCount === 0) return; setSlideIdx(((i % slideCount) + slideCount) % slideCount); };
  const prevSlide = () => goTo(slideIdx - 1);
  const nextSlide = () => goTo(slideIdx + 1);

  const onTouchStart = e => { touchStartXRef.current = e.touches?.[0]?.clientX ?? null; };
  const onTouchEnd = e => {
    const startX = touchStartXRef.current;
    touchStartXRef.current = null;
    if (startX == null) return;
    const endX = e.changedTouches?.[0]?.clientX ?? startX;
    const dx = endX - startX;
    if (Math.abs(dx) < 30) return;
    if (dx < 0) nextSlide(); else prevSlide();
  };

  const navBtnStyle = {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)',
    width: 26, height: 26, borderRadius: '50%', border: 'none',
    background: 'rgba(0,0,0,0.35)', color: '#fff', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
    lineHeight: 1, zIndex: 2,
  };

  return (
    <div style={{ position: 'relative', width: phoneWidth }}>
    <div style={{ width: phoneWidth, background: '#fff', borderRadius: 28, boxShadow: isCenter ? '0 20px 60px rgba(0,0,0,0.18), 0 4px 20px rgba(0,0,0,0.10)' : '0 8px 30px rgba(0,0,0,0.10)', overflow: 'hidden', border: (isSelected && !isDisabled) ? `2.5px solid ${RED}` : '2.5px solid #e8e0e0', transition: 'box-shadow 0.45s ease, border-color 0.45s ease, filter 0.3s ease', filter: isDisabled ? 'grayscale(0.6) brightness(0.94)' : 'none' }}>
      <div style={{ background: '#fff', padding: '10px 14px 6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#222' }}>9:30</span>
        <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#222' }} />
        <div style={{ width: 16, height: 10, borderRadius: 2, border: '1.5px solid #666', display: 'flex', alignItems: 'center', padding: '1px 2px' }}><div style={{ flex: 1, height: '100%', background: '#666', borderRadius: 1 }} /></div>
      </div>
      <div style={{ background: '#fff', padding: '4px 12px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
        <span style={{ fontSize: 11, color: '#444' }}>← Coupon Details</span>
        <span style={{ fontSize: 16, color: '#444' }}>⋮</span>
      </div>
      <div onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
        style={{ width: '100%', position: 'relative', paddingBottom: '100%', background: 'linear-gradient(135deg,#f5e0e0,#ead5d5)', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0 }}>
          {slideCount > 0 ? (
            <img src={slides[slideIdx]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          ) : (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', fontSize: 11 }}>No image</div>
          )}
          {loading && <div style={{ position: 'absolute', inset: 0, background: 'rgba(240,230,230,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ width: 22, height: 22, border: `2.5px solid ${RED}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'p4spin 0.8s linear infinite' }} /></div>}
          {slideCount > 1 && (
            <>
              <button type="button" onClick={e => { e.stopPropagation(); prevSlide(); }} style={{ ...navBtnStyle, left: 8 }} aria-label="Previous image">‹</button>
              <button type="button" onClick={e => { e.stopPropagation(); nextSlide(); }} style={{ ...navBtnStyle, right: 8 }} aria-label="Next image">›</button>
              <div style={{ position: 'absolute', bottom: 8, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 6, zIndex: 2 }}>
                {slides.map((_, i) => (
                  <div key={i} onClick={e => { e.stopPropagation(); goTo(i); }}
                    style={{ width: i === slideIdx ? 16 : 6, height: 6, borderRadius: 999, background: i === slideIdx ? '#fff' : 'rgba(255,255,255,0.55)', cursor: 'pointer', transition: 'all 0.25s ease', boxShadow: '0 0 2px rgba(0,0,0,0.25)' }} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      <div style={{ padding: '12px 14px' }}>
        <div style={{ textAlign: 'center', marginBottom: 4, minHeight: 36 }}>
          {loading ? <div style={{ height: 28, background: '#f0e8e8', borderRadius: 6, margin: '0 auto', width: '70%', animation: 'p4pulse 1.2s ease infinite' }} /> : discountDisplay()}
        </div>
        <div style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: RED, marginBottom: 8, minHeight: 16 }}>
          {loading ? <div style={{ height: 12, background: '#f0e8e8', borderRadius: 4, margin: '4px auto', width: '80%' }} /> : card?.title || ''}
        </div>
        <div style={{
  fontSize: 10, color: '#666', lineHeight: 1.5, textAlign: 'center', marginBottom: 10,
  height: 72, // 3 lines à 1.5 line-height * 10px font ≈ 72
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
}}>
          {loading ? <><div style={{ height: 9, background: '#f5f5f5', borderRadius: 3, marginBottom: 3 }} /><div style={{ height: 9, background: '#f5f5f5', borderRadius: 3, marginBottom: 3 }} /><div style={{ height: 9, background: '#f5f5f5', borderRadius: 3, width: '70%', margin: '0 auto' }} /></> : card?.description || ''}
        </div>
        <div style={{ borderTop: '1.5px dashed #e0d0d0', margin: '8px 0' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#f0e8e8', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {businessLogo ? <img src={businessLogo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 9, color: '#aaa' }}>B</span>}
          </div>
          <span style={{ fontSize: 10, fontWeight: 600, color: '#333' }}>{businessName}</span>
        </div>
        <div style={{ border: '1px solid #e0d5d5', borderRadius: 999, padding: '4px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 9, color: '#888' }}>Deal Value</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#333' }}>{loading ? '—' : card?.dealValue || '—'}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 8, color: '#bbb' }}>↗</span>
          <span style={{ fontSize: 8, color: '#999' }}>{!loading && card?.meta?.expiryDays ? `Valid until ${new Date(Date.now() + card.meta.expiryDays * 86400000).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}` : ''}</span>
          <span style={{ fontSize: 8, color: '#bbb' }}>ⓘ</span>
        </div>
        <div style={{ background: RED, borderRadius: 10, padding: '9px', textAlign: 'center', color: '#fff', fontSize: 11, fontWeight: 700 }}>Redeem Now</div>
      </div>
    </div>

    {isDisabled && (
      <div style={{
        position: 'absolute', inset: 0, borderRadius: 28,
        background: 'rgba(20,20,20,0.15)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        pointerEvents: 'none', padding: '0 24px', textAlign: 'center',
      }}>
        <div style={{
          background: 'rgba(26,26,26,0.9)', color: '#fff',
          padding: '12px 18px', borderRadius: 14,
          fontSize: 12.5, fontWeight: 700, lineHeight: 1.4,
          letterSpacing: '0.2px', boxShadow: '0 10px 28px rgba(0,0,0,0.3)',
        }}>
          🔒 Points System<br />Coming Soon
        </div>
      </div>
    )}
    </div>
  );
}

// ── Mobile / Tablet: infinite scroll-snap carousel ───────────────────────────
// Renders 9 slots: [clone,clone,clone, real0,real1,real2, clone,clone,clone]
// Starts at the middle copy. When user reaches either clone boundary, we
// silently jump back to the equivalent real slot — seamless infinite loop.
const N = 3;

function MobileCarousel({
  cards, centerIdx, setCenterIdx, selected, setSelected,
  loading, heroUrl, heroImages, businessName, businessLogo,
  phoneWidth, showActions, setShowActions,
  isSavingDraft, handleEditClick, handleSaveDraft, actionBtnStyle,
  aiCards,
}) {
  const scrollRef = useRef(null);
  const itemRefs = useRef([]);
  const [virtualIdx, setVirtualIdx] = useState(centerIdx + N);
  const isJumping = useRef(false);

  // 9-slot array: pre-clones | real cards | post-clones
  const slots = useMemo(() => {
    const c = cards.length === 3 ? cards : [null, null, null];
    return [...c, ...c, ...c];
  }, [cards]);

  const realOf = (vi) => ((vi % N) + N) % N;

  // Dot-tap: jump smoothly to the equivalent slot in the middle copy
  useEffect(() => {
    const target = itemRefs.current[centerIdx + N];
    if (target && scrollRef.current) {
      target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
    setVirtualIdx(centerIdx + N);
  }, [centerIdx]);

  // Scroll listener: update active card + loop when reaching clones
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    let ticking = false;

    const onScroll = () => {
      if (ticking || isJumping.current) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        if (isJumping.current) return;

        // Find closest slot to viewport center
        const cRect = container.getBoundingClientRect();
        const cx = cRect.left + cRect.width / 2;
        let best = virtualIdx, bestDist = Infinity;
        itemRefs.current.forEach((el, i) => {
          if (!el) return;
          const r = el.getBoundingClientRect();
          const dist = Math.abs(r.left + r.width / 2 - cx);
          if (dist < bestDist) { bestDist = dist; best = i; }
        });

        if (best === virtualIdx) return;

        const realIdx = realOf(best);
        setVirtualIdx(best);
        setCenterIdx(realIdx);
        if (!loading && aiCards[realIdx] && !isPointsCard(aiCards[realIdx])) setSelected(aiCards[realIdx].id);
        setShowActions(false);

        // If in clone zone, silently teleport to matching real slot
        if (best < N || best >= N * 2) {
          const targetVi = realIdx + N;
          const targetEl = itemRefs.current[targetVi];
          if (targetEl) {
            isJumping.current = true;
            const itemLeft = targetEl.offsetLeft;
            const containerW = container.offsetWidth;
            const itemW = targetEl.offsetWidth;
            container.scrollLeft = itemLeft - (containerW - itemW) / 2;
            setVirtualIdx(targetVi);
            setTimeout(() => { isJumping.current = false; }, 80);
          }
        }
      });
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [virtualIdx, loading, aiCards, setCenterIdx, setSelected, setShowActions]);

  // Initialize scroll to middle copy on first render
  useEffect(() => {
    const container = scrollRef.current;
    const target = itemRefs.current[centerIdx + N];
    if (container && target) {
      const itemLeft = target.offsetLeft;
      const containerW = container.offsetWidth;
      const itemW = target.offsetWidth;
      container.scrollLeft = itemLeft - (containerW - itemW) / 2;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once loading finishes, snap (no animation) to the corrected centerIdx —
  // handles the case where generation put the disabled Points card first.
  const hasSnappedAfterLoadRef = useRef(false);
  useEffect(() => {
    if (loading || hasSnappedAfterLoadRef.current) return;
    hasSnappedAfterLoadRef.current = true;
    const container = scrollRef.current;
    const target = itemRefs.current[centerIdx + N];
    if (container && target) {
      const itemLeft = target.offsetLeft;
      const containerW = container.offsetWidth;
      const itemW = target.offsetWidth;
      container.scrollLeft = itemLeft - (containerW - itemW) / 2;
    }
    setVirtualIdx(centerIdx + N);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  return (
    <div>
      <style>{`.p4-sc::-webkit-scrollbar{display:none}.p4-sc{-ms-overflow-style:none;scrollbar-width:none}`}</style>
      <div
        ref={scrollRef}
        className="p4-sc"
        style={{
          display: 'flex',
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          gap: 16,
          paddingTop: 12,
          paddingBottom: 24,
          paddingLeft: `calc(50% - ${phoneWidth / 2}px)`,
          paddingRight: `calc(50% - ${phoneWidth / 2}px)`,
          boxSizing: 'border-box',
        }}
      >
        {slots.map((card, vi) => {
          const realIdx = realOf(vi);
          const isC = vi === virtualIdx;
          const isSelected = !loading && aiCards[realIdx] && aiCards[realIdx].id === selected && !isPointsCard(aiCards[realIdx]);

          return (
            <div
              key={vi}
              ref={el => { itemRefs.current[vi] = el; }}
              onClick={() => {
                if (isC) setShowActions(v => !v);
                else setShowActions(false);
              }}
              style={{
                scrollSnapAlign: 'center',
                flexShrink: 0,
                width: phoneWidth,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 14,
                transition: 'opacity 0.25s ease, transform 0.25s ease',
                opacity: isC ? 1 : 0.45,
                transform: isC ? 'scale(1)' : 'scale(0.90)',
                cursor: 'pointer',
              }}
            >
              <PhoneMockup
                card={card} scale={1} isSelected={isSelected && isC}
                loading={loading} heroUrl={heroUrl} images={heroImages}
                businessName={businessName} businessLogo={businessLogo}
                phoneWidth={phoneWidth}
              />

              {/* Edit / Draft only on the real center slot */}
              {isC && vi >= N && vi < N * 2 && !loading && card && (
                <div style={{
                  display: 'flex', flexDirection: 'row', gap: 10,
                  opacity: showActions ? 1 : 0,
                  pointerEvents: showActions ? 'auto' : 'none',
                  transition: 'opacity 0.25s ease',
                  whiteSpace: 'nowrap',
                }}>
                  <button type="button" onClick={e => { e.stopPropagation(); handleEditClick(); }} style={actionBtnStyle}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    Edit
                  </button>
                  <button type="button" onClick={e => { e.stopPropagation(); handleSaveDraft(); }} disabled={isSavingDraft}
                    style={{ ...actionBtnStyle, opacity: isSavingDraft ? 0.7 : 1, cursor: isSavingDraft ? 'not-allowed' : 'pointer' }}>
                    {isSavingDraft
                      ? <span style={{ width: 14, height: 14, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'p4spin 0.8s linear infinite' }} />
                      : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                          <polyline points="17 21 17 13 7 13 7 21" />
                          <polyline points="7 3 7 8 15 8" />
                        </svg>}
                    {isSavingDraft ? 'Saving…' : 'Draft'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
// ── Main Component ────────────────────────────────────────────────────────────
export default function Page4_RecommendedCoupons({
  goal, speed, scope, item, exclusivity = '', selectedTemplate,
  onNext, onBack, onClose, onSubmit, onSaveDraft, isPremiumUser, currencyCode = 'USD',
  businessName: businessNameProp, city: cityProp,
  userType = 'basic', audience = [], repeatBusiness = null, slowPeriods = null, constraints = null,
}) {
  const width = useWindowWidth();
  const isMobile = width < 641;
  const isTablet = width < 821;
  const pagePaddingH = isMobile ? 14 : isTablet ? 24 : 40;

  // Phone sizing — shrink phone card on smaller screens
  const phoneWidth = isMobile ? Math.min(width - 28, 260) : isTablet ? 260 : 300;

  const dispatch = useDispatch();
  const businessInfo = useSelector(s => s.businessInfo?.data || {});
  const businessLogo = businessInfo.logo || businessInfo.logoUrl || businessInfo.logo_url || businessInfo.business_logo || null;
  const businessName = businessNameProp || getBusinessName(businessInfo);
  const city = cityProp || getCity(businessInfo);
  const address = getAddress(businessInfo);
  const couponImages = useSelector(s => s.couponImages || {});
  const heroSlot = couponImages[HERO_SLOT_ID] || {};
  const initialHeroUrl = item?.primaryImage || (Array.isArray(item?.images) && item.images.length > 0 ? item.images[0] : null);
  const [heroUrl, setHeroUrl] = useState(initialHeroUrl || heroSlot.url || null);
  useEffect(() => { if (heroSlot?.url) setHeroUrl(heroSlot.url); }, [heroSlot?.url]);

  const heroImages = useMemo(() => {
    const fromSlots = [1, 2, 3].map(slot => couponImages[slot]?.url).filter(Boolean);
    if (fromSlots.length > 0) return fromSlots;
    if (Array.isArray(item?.images) && item.images.length > 0) return item.images.filter(Boolean);
    return heroUrl ? [heroUrl] : [];
  }, [couponImages, item?.images, heroUrl]);

  const scNorm = normalizeScope(scope);
  const gNorm = normalizeGoal(goal);
  const sNorm = normalizeSpeed(speed);
  const itemName = scNorm === 'Storewide Promotion' ? 'Storewide' : item?.name || 'Featured Item';
  const itemPrice = Number(item?.price || 0) || 0;
  const itemDescription = item?.description || '';

  const recommendations = useMemo(() => {
    try { return generateRecommendations(gNorm, sNorm, scNorm, itemPrice, exclusivity, userType); }
    catch { return [{ type: 'percent', value: 10, minSpend: 0, badge: 'Standard', meta: { expiryType: 'date', expiryDays: 30, perUserLimit: 'Unlimited', redemptionType: 'Auto', redeemableWithPoints: true, reservationHours: null, exclusivity: '' } }, { type: 'percent', value: 20, minSpend: 50, badge: 'Aggressive', meta: { expiryType: 'date', expiryDays: 14, perUserLimit: 2, redemptionType: 'Auto', redeemableWithPoints: true, reservationHours: null, exclusivity: '' } }, { type: 'points', points: 500, minSpend: 25, badge: 'Points', meta: { expiryType: 'date', expiryDays: 30, perUserLimit: 1, redemptionType: 'In-App', redeemableWithPoints: false, reservationHours: null, exclusivity: '' } }]; }
  }, [gNorm, sNorm, scNorm, itemPrice, exclusivity, userType]);

  const cacheKey = useMemo(() => JSON.stringify({
    businessName, city, itemName, itemPrice, itemDescription,
    scNorm, gNorm, sNorm, exclusivity, userType,
    audience: Array.isArray(audience) ? [...audience].sort() : audience,
    repeatFrequency: repeatBusiness?.frequency || null,
    slowDays: Array.isArray(slowPeriods?.days) ? [...slowPeriods.days].sort() : null,
    slowTimeBlocks: Array.isArray(slowPeriods?.timeBlocks) ? [...slowPeriods.timeBlocks].sort() : null,
    minSpend: constraints?.minSpend ?? null,
    purchaseQty: constraints?.purchaseQty ?? null,
  }), [businessName, city, itemName, itemPrice, itemDescription, scNorm, gNorm, sNorm, exclusivity, userType, audience, repeatBusiness, slowPeriods, constraints]);

  const [aiCards, setAiCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(selectedTemplate ?? null);
  const [centerIdx, setCenterIdx] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [sendPush, setSendPush] = useState(false);
  const generationNonceRef = useRef(0);

  const selectedCard = useMemo(() => (aiCards || []).find(c => c.id === selected) || null, [selected, aiCards]);
  // Defensive: selectedCard should never be the Points card (all selection
  // paths already skip it), but this covers edge cases like a `selectedTemplate`
  // passed in from a prior session pointing at one.
  const canPublish = !!selectedCard && !isPointsCard(selectedCard);

  function getExpiryForRepeatFrequency(freq) {
    const f = String(freq || '').toLowerCase();
    if (f.includes('multiple') || f.includes('week')) return { expiryType: 'amount', totalCoupons: 500, perUserLimit: 'Unlimited' };
    if (f.includes('once') || f.includes('month')) return { expiryType: 'date', expiryDays: 60, perUserLimit: 2 };
    if (f.includes('season')) return { expiryType: 'date', expiryDays: 90, perUserLimit: 1 };
    return { expiryType: 'date', expiryDays: 30, perUserLimit: null };
  }

  useEffect(() => {
    const cached = p4GenerationCache;
    if (cached && cached.key === cacheKey && Array.isArray(cached.cards) && cached.cards.length > 0) {
      setAiCards(cached.cards);
      const isValidSelected = selected != null && cached.cards.some(c => c.id === selected && !isPointsCard(c));
      if (isValidSelected) {
        const idx = cached.cards.findIndex(c => c.id === selected);
        setCenterIdx(idx === -1 ? 0 : idx);
      } else {
        const firstSelectableIdx = cached.cards.findIndex(r => !isPointsCard(r));
        const initialIdx = firstSelectableIdx === -1 ? 0 : firstSelectableIdx;
        setSelected(cached.cards[initialIdx]?.id ?? null);
        setCenterIdx(initialIdx);
      }
    } else {
      handleGenerate();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateTitleUniqueness = (newTitle, previousTitles, usedStarters, usedEndings, usedKeywords) => {
    const titleLower = newTitle.toLowerCase();
    if (previousTitles.some(pt => pt.text.toLowerCase() === titleLower)) return { isValid: false, reason: 'Exact duplicate title' };
    const words = newTitle.split(' ');
    const firstWord = words[0]?.toLowerCase().replace(/[^a-z]/g, '');
    const lastWord = words[words.length - 1]?.toLowerCase().replace(/[^a-z]/g, '');
    if (firstWord && firstWord.length > 2 && usedStarters.has(firstWord)) return { isValid: false, reason: `Starts with forbidden word: ${firstWord}` };
    if (lastWord && lastWord.length > 2 && usedEndings.has(lastWord)) return { isValid: false, reason: `Ends with forbidden word: ${lastWord}` };
    const titleKeywords = words.map(w => w.toLowerCase().replace(/[^a-z]/g, '')).filter(w => w.length > 3);
    const overlapCount = titleKeywords.filter(k => usedKeywords.has(k)).length;
    const overlapPercent = titleKeywords.length > 0 ? (overlapCount / titleKeywords.length) * 100 : 0;
    if (overlapPercent > 20) return { isValid: false, reason: `Too similar: ${overlapPercent.toFixed(0)}% keyword overlap` };
    return { isValid: true, reason: '' };
  };

  const handleGenerate = async () => {
    setLoading(true);
    generationNonceRef.current += 1;
    const targetSegment = Array.isArray(audience) && audience.length > 0 ? audience.join(', ') : '';
    const frequencyTarget = repeatBusiness?.frequency || '';
    const slowDaysArr = slowPeriods?.days || [];
    const slowTimeBlocks = slowPeriods?.timeBlocks || [];
    const timeRange = Array.isArray(slowTimeBlocks) && slowTimeBlocks.length > 0 ? slowTimeBlocks.filter(t => t !== 'Custom').join(', ') : '';
    let expiryOverride = null;
    if (gNorm === 'Repeat Business' && repeatBusiness?.frequency) expiryOverride = getExpiryForRepeatFrequency(repeatBusiness.frequency);
    const dealSig = recommendations.map(r => `${couponTypeForReward(r)}:${discountValueForReward(r) ?? ''}:${r.minSpend ?? ''}`).join('|');
    const seed = `${businessName}|${city}|${itemName}|${itemPrice}|${dealSig}|${Date.now()}|${Math.random()}|nonce${generationNonceRef.current}`;
    const titleAngles = pickDistinctTitleAngles(seed, 3);
    try {
      const results = [], usedStarters = new Set(), usedEndings = new Set(), usedKeywords = new Set(), previousTitles = [];
      for (let idx = 0; idx < recommendations.length; idx++) {
        const r = recommendations[idx];
        const pct2 = couponTypeForReward(r), pdv = discountValueForReward(r);
        const redemptionType = String(r.meta.redemptionType || '').toLowerCase().includes('auto') ? 'auto' : 'manual';
        const cardExclusivity = r.meta.exclusivity;
        const fet = expiryOverride?.expiryType || r.meta.expiryType;
        const fed = fet === 'date' ? (expiryOverride?.expiryDays || r.meta.expiryDays) : undefined;
        const ftc = expiryOverride?.totalCoupons || r.meta.totalCoupons;
        const fpul = expiryOverride?.perUserLimit != null ? expiryOverride.perUserLimit : r.meta.perUserLimit;
        const pult = fpul === 'Unlimited' ? 'unlimited' : 'multiple', pulc = fpul === 'Unlimited' ? '' : fpul;
        let ced = '';
        if (fet === 'date' && fed && Number(fed) > 0) { const d = new Date(); d.setDate(d.getDate() + Number(fed)); ced = d.toISOString().split('T')[0]; }
        const avCtx = previousTitles.length === 0 ? '' : ['PREVIOUS TITLES:', ...previousTitles.map((pt, i) => `  ${i + 1}. "${pt.text}"`), 'REQUIRED: Use a DIFFERENT structure.'].join('\n');
        const pd = { businessName, city, address: address || businessName, location: address || businessName, itemName, itemDescription, itemPrice: itemPrice || null, couponType: pct2, discountValue: pdv, scope: scNorm, goal: gNorm, speed: sNorm, minSpend: r.minSpend > 0 ? r.minSpend : constraints?.minSpend || null, purchaseQty: constraints?.purchaseQty || null, slowDays: slowDaysArr, timeRange, reservationHours: r.meta.reservationHours || null, redeemableWithPoints: r.meta.redeemableWithPoints ?? false, expiryType: fet, expiryDate: ced, totalQuantity: ftc || '', perUserLimitType: pult, perUserLimitCount: pulc, redemptionType, exclusivity: cardExclusivity || '', exclusiveFollowing: cardExclusivity === 'followers', exclusivePremium: cardExclusivity === 'premium', targetSegment: cardExclusivity === 'premium' ? 'Premium Members' : cardExclusivity === 'followers' ? 'Followers' : targetSegment, frequencyTarget, titleAngle: titleAngles[idx] || TITLE_ANGLE_POOL[idx % TITLE_ANGLE_POOL.length], titleAngleKey: `card_${idx}`, variantIndex: String(idx + 1), avoidanceContext: avCtx, rules: 'Respect all length limits; avoid placeholders; must reflect provided context.' };
        const res = await dispatchAsAsync(aiActions.generateText(`all_${idx}`, { prompt: allCouponPrompt(pd), type: 'all_coupon', length: 0 }), dispatch);
        const fb = buildCopy(r, { itemName, scope: scNorm, goal: gNorm, speed: sNorm, exclusivity: cardExclusivity });
        const parsed = parseAllCouponText(res?.text || '');
        let title = parsed.title || fb.title, description = parsed.description || fb.desc, terms = parsed.terms || fb.terms;
        let retry = 0;
        while (retry < 3) {
          const v = validateTitleUniqueness(title, previousTitles, usedStarters, usedEndings, usedKeywords);
          if (v.isValid) break;
          const rr = await dispatchAsAsync(aiActions.generateText(`all_${idx}_r${retry}`, { prompt: allCouponPrompt({ ...pd, avoidanceContext: `${avCtx}\nPrevious attempt "${title}" REJECTED: ${v.reason}. Must be completely different.` }), type: 'all_coupon', length: 0 }), dispatch);
          const rp = parseAllCouponText(rr?.text || ''); title = rp.title || title; description = rp.description || description; terms = rp.terms || terms; retry++;
        }
        const tw = title.split(' '), fw = tw[0] || '', lw = tw[tw.length - 1] || '', cl = lw.toLowerCase().replace(/[^a-z]/g, '');
        if (fw && fw.length > 2) usedStarters.add(fw);
        if (cl && cl.length > 2) usedEndings.add(cl);
        const cw = new Set(['a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'from']);
        tw.forEach(w => { const c2 = w.toLowerCase().replace(/[^a-z]/g, ''); if (c2.length > 3 && !cw.has(c2)) usedKeywords.add(c2); });
        previousTitles.push({ text: title, firstWord: fw, lastWord: cl });
        let dv = '';
        if (r.type === 'percent') dv = `${r.value}% off`;
        if (r.type === 'amount') dv = `${currencyFmt(r.amount, currencyCode)} off`;
        if (r.type === 'points') dv = `${r.points?.toLocaleString()} points` + (r.minSpend > 0 ? ` (min ${currencyFmt(r.minSpend, currencyCode)})` : '');
        results.push({ id: idx + 1, badge: r.badge, title, description, terms, couponType: r.type === 'points' ? 'Points Back' : r.type === 'percent' ? 'Percent Off' : 'Dollar Off', dv, dealValue: dv, meta: { ...r.meta, expiryType: fet, expiryDays: fet === 'date' ? fed : undefined, totalCoupons: fet === 'amount' ? ftc : null, perUserLimit: fpul }, raw: r });
      }
      setAiCards(results);
      p4GenerationCache = { key: cacheKey, cards: results };
      const firstSelectableIdx = results.findIndex(r => !isPointsCard(r));
      const initialIdx = firstSelectableIdx === -1 ? 0 : firstSelectableIdx;
      setSelected(results[initialIdx]?.id ?? null);
      setCenterIdx(initialIdx);
      setShowActions(false);
    } catch (err) {
      console.error('AI error:', err);
      toast.error('AI failed. Using defaults.');
    } finally { setLoading(false); }
  };

  const handleCardClick = (cardIdx) => {
    if (isAnimating) return;
    if (cardIdx === centerIdx) { setShowActions(v => !v); return; }
    setShowActions(false);
    setIsAnimating(true);
    setTimeout(() => {
      setCenterIdx(cardIdx);
      if (!loading && aiCards[cardIdx] && !isPointsCard(aiCards[cardIdx])) setSelected(aiCards[cardIdx].id);
      setIsAnimating(false);
    }, 450);
  };

  const buildCouponPayload = (card) => {
    const r = card.raw;
    const typeForP5 = r.type === 'percent' ? 'percentage' : r.type === 'amount' ? 'dollar' : 'points';
    const discountForP5 = r.type === 'percent' ? r.value : r.type === 'amount' ? r.amount : r.points;
    const itemTypeForP5 = scNorm === 'Individual Item' ? 'individual' : scNorm === 'Product Line' ? 'category' : 'all';
    const cm = card.meta, et = cm?.expiryType || 'date';
    return { startFromScratch: false, title: card.title, description: card.description, terms: card.terms, couponType: typeForP5, discountValue: discountForP5, redeemableWithPoints: cm.redeemableWithPoints ?? false, itemType: itemTypeForP5, itemName, itemPrice, itemDescription, goalKey: gNorm, businessName, city, expiryType: et, expiryDays: et === 'date' ? cm?.expiryDays : null, totalCoupons: et === 'amount' ? cm?.totalCoupons : null, limitType: cm.perUserLimit === 'Unlimited' ? 'unlimited' : 'custom', limitQty: cm.perUserLimit === 'Unlimited' ? null : cm.perUserLimit, reservationHours: cm.reservationHours ?? 4, meta: cm, exclusivity: cm.exclusivity || '', redemptionType: cm.redemptionType || 'auto', minSpend: r.minSpend ?? null, images: heroImages, heroImagePreview: heroUrl || null };
  };

  const handleNext = () => { if (!canPublish) return; setShowConfirm(true); };

  const handleEditClick = () => {
    if (!selectedCard) return;
    onNext?.({ recommendedTemplate: selected, startFromScratch: false, goToEdit: true, couponPayload: buildCouponPayload(selectedCard), heroImagePreview: heroUrl || null });
  };

  const uploadLocalImages = async () => {
    const results = [];
    for (let i = 0; i < Math.min(heroImages.length, 3); i++) {
      const url = heroImages[i];
      if (!url) continue;
      if (/^https?:\/\//i.test(url)) { results.push(url); continue; }
      if (url.startsWith('blob:') || url.startsWith('data:')) {
        try {
          const resp = await fetch(url);
          const blob = await resp.blob();
          const slotId = i + 1;
          const r = await dispatchAsAsync(couponImageActions.uploadCouponImage(slotId, 0, slotId, blob), dispatch);
          if (r?.image_url) results.push(r.image_url);
        } catch { }
      }
    }
    return results;
  };

  const mapTypeP4 = t => (t === 'percentage' ? 'percentage_off' : t === 'dollar' ? 'amount_off' : t === 'points' ? 'points_back' : null);
  const clampStr = (s = '', m = 600) => { const str = String(s || ''); return str.length > m ? str.slice(0, m) : str; };
  const addDaysP4 = (n = 0) => { const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + Number(n || 0)); return d; };
  const formatExpirationDate = (d) => { const pad = n => String(n).padStart(2, '0'); const om = d.getTimezoneOffset(), sg = om > 0 ? '-' : '+'; return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}${sg}${pad(Math.floor(Math.abs(om) / 60))}:${pad(Math.abs(om) % 60)}`; };

  const buildApiPayload = (card, couponImageUrls, state = 'published') => {
    const cp = buildCouponPayload(card);
    const bt = mapTypeP4(cp.couponType);
    const n0 = v => { const n = Number(v); return isFinite(n) ? n : 0; };
    const nN = v => { if (v === '' || v === null || v === undefined) return null; const n = Number(v); return isFinite(n) ? n : null; };
    let expiry_type = 'date', expiration_date = null, expiration_quantity = null;
    if (cp.expiryType === 'amount') { expiry_type = 'amount_limit'; expiration_quantity = nN(cp.totalCoupons); }
    else { expiry_type = 'date'; const days = Number(cp.expiryDays) || 30; expiration_date = formatExpirationDate(addDaysP4(days)); }
    let user_limit = 0;
    if (cp.limitType === 'custom') { let n = Number(cp.limitQty || 0); if (isNaN(n) || n < 1) n = 1; user_limit = n; }
    const taxes = item?.taxes || {};
    const showPrice = itemPrice > 0;
    const sids = Array.isArray(item?.subcategories) ? item.subcategories.map(s => (typeof s === 'object' && s !== null ? s.id : s)) : [];
    return { title: clampStr(cp.title, 50), description: clampStr(cp.description, 1000), type: bt, percentage_off: bt === 'percentage_off' ? n0(cp.discountValue) : 0, amount_off: bt === 'amount_off' ? n0(cp.discountValue) : 0, points_back: bt === 'points_back' ? n0(cp.discountValue) : 0, show_price_on_coupon: showPrice, price: showPrice ? String(itemPrice) : null, auto_accept: (cp.redemptionType || 'auto') === 'auto', reserved_hours: cp.reservationHours ?? 4, payment_by_points: !!cp.redeemableWithPoints, user_limit, expiry_type, expiration_date, expiration_quantity, terms_and_conditions: cp.terms || '', category: item?.category?.id ?? item?.categoryId ?? undefined, subcategories: sids.filter(v => v !== null && v !== undefined && v !== '' && v !== 'None').map(v => Number(v)).filter(n => isFinite(n) && n > 0), coupon_images: couponImageUrls, is_exclusive_paid: cp.exclusivity === 'premium', is_exclusive_followers: cp.exclusivity === 'followers', gst: nN(taxes.gst), hst: nN(taxes.hst), pst: nN(taxes.pst), qst: nN(taxes.qst), rst: nN(taxes.rst), goal_key: cp.goalKey ?? null, offer_impact: null, state };
  };

  const [isPublishing, setIsPublishing] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const handleConfirmProceed = async () => {
    if (!termsAccepted || !canPublish) return;
    setIsPublishing(true);
    try {
      const couponImageUrls = await uploadLocalImages();
      const payload = { ...buildApiPayload(selectedCard, couponImageUrls), sendPushNotification: sendPush };
      if (onSubmit) onSubmit(payload); else onNext?.(payload);
      setShowConfirm(false); setTermsAccepted(false); setSendPush(false);
    } catch (e) { console.error(e); toast.error('Failed to publish coupon'); }
    finally { setIsPublishing(false); }
  };

  const handleSaveDraft = async () => {
    if (!selectedCard) return;
    if (!onSaveDraft) { onBack && onBack('draft'); return; }
    setIsSavingDraft(true);
    try {
      const couponImageUrls = await uploadLocalImages();
      const payload = buildApiPayload(selectedCard, couponImageUrls, 'draft');
      onSaveDraft(payload);
    } catch (e) { console.error(e); toast.error('Failed to save draft'); }
    finally { setIsSavingDraft(false); }
  };

  const cards = loading ? [null, null, null] : aiCards.slice(0, 3);

  const getCardStyle = (cardIdx) => {
    const leftIdx = (centerIdx + 2) % 3;
    const isC = cardIdx === centerIdx;
    const isL = cardIdx === leftIdx;
    // On mobile: tighter side offsets because phone cards are smaller
    const sideOffset = isMobile ? phoneWidth * 0.72 : isTablet ? 250 : 320;
    const xOffset = isC ? 0 : isL ? -sideOffset : sideOffset;
    const scale = isC ? 1 : isMobile ? 0.72 : 0.78;
    const opacity = isC ? 1 : 0.6;
    const zIdx = isC ? 3 : 1;
    return { xOffset, scale, opacity, zIdx, isC };
  };

  // Carousel height: shrinks on mobile
  const carouselHeight = isMobile ? phoneWidth * 2.6 : isTablet ? 600 : 700;

  const actionBtnStyle = {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: isMobile ? '10px 18px' : '13px 26px',
    background: RED, color: '#fff',
    border: 'none', borderRadius: 36,
    fontSize: isMobile ? 13 : 15, fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 6px 18px rgba(192,57,43,0.35)',
    minWidth: isMobile ? 100 : 130, whiteSpace: 'nowrap',
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(150deg,#f7e8e8 0%,#eddcdc 50%,#e6cdcd 100%)', fontFamily: "'Inter','Segoe UI',sans-serif", padding: '0 0 40px', boxSizing: 'border-box' }}>
      <style>{`@keyframes p4spin{to{transform:rotate(360deg)}}@keyframes p4pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>

      {/* ── Top bar ── */}
      <div style={{ padding: `20px ${pagePaddingH}px 0`, position: 'relative' }}>
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => {
              const n = i + 1, ia = n === CURRENT_STEP, id = n < CURRENT_STEP;
              return (<React.Fragment key={n}><div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, background: ia || id ? RED : 'transparent', border: ia || id ? 'none' : '2px solid #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: ia || id ? '#fff' : '#bbb' }}>{n}</div>{i < TOTAL_STEPS - 1 && <div style={{ flex: 1, height: 2, background: id ? RED : '#ddd' }} />}</React.Fragment>);
            })}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <h2 style={{ margin: '0 0 8px', fontSize: isMobile ? 18 : isTablet ? 20 : 24, fontWeight: 800, color: '#1a1a1a', letterSpacing: '-0.3px' }}>
            Recommended Coupons: Designed for Your Success
          </h2>
          {!loading && aiCards.length > 0 && (
            <button type="button" onClick={handleGenerate} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', borderRadius: 999,
              border: `1.5px solid ${RED}`, background: '#fff', color: RED,
              fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              flexShrink: 0, marginBottom: 8,
            }}>
              <RefreshCw size={13} /> Regenerate
            </button>
          )}
        </div>
        <p style={{ margin: '0 0 24px', fontSize: 13, color: '#888', lineHeight: 1.65, maxWidth: 620 }}>
          Based on your goal (<b>{gNorm}</b>), speed (<b>{sNorm}</b>), and scope (<b>{scNorm}</b>), we generated three high-performing options. {isMobile ? 'Tap a phone to select.' : 'Click a phone to select it.'}
        </p>
      </div>

      {loading && <LoadingLabels />}

      {/* ── Carousel — scroll-snap on mobile/tablet, CSS-transform on desktop ── */}
      {(isMobile || isTablet) ? (
        <MobileCarousel
          cards={cards}
          centerIdx={centerIdx} setCenterIdx={setCenterIdx}
          selected={selected} setSelected={setSelected}
          loading={loading} heroUrl={heroUrl} heroImages={heroImages}
          businessName={businessName} businessLogo={businessLogo}
          phoneWidth={phoneWidth}
          showActions={showActions} setShowActions={setShowActions}
          isSavingDraft={isSavingDraft}
          handleEditClick={handleEditClick}
          handleSaveDraft={handleSaveDraft}
          actionBtnStyle={actionBtnStyle}
          aiCards={aiCards}
        />
      ) : (
        /* ── DESKTOP: original CSS-transform carousel ── */
        <div style={{ position: 'relative', height: carouselHeight, maxWidth: 1100, margin: '0 auto 16px', overflow: 'hidden' }}>
          {[0, 1, 2].map(cardIdx => {
            const card = cards[cardIdx];
            const isSelected = !loading && card && card.id === selected && !isPointsCard(card);
            const { xOffset, scale, opacity, zIdx, isC } = getCardStyle(cardIdx);
            return (
              <div
                key={cardIdx}
                onClick={() => handleCardClick(cardIdx)}
                style={{
                  position: 'absolute', left: '50%', top: '50%',
                  transform: `translate(calc(-50% + ${xOffset}px), -50%) scale(${scale})`,
                  opacity, zIndex: zIdx,
                  transition: 'transform 0.45s cubic-bezier(.2,.8,.3,1), opacity 0.45s ease',
                  cursor: 'pointer',
                  willChange: 'transform, opacity',
                }}
              >
                {isC && !loading && card && (
                  <div style={{
                    position: 'absolute', top: '15%', left: 'calc(100% + 32px)',
                    transform: showActions ? 'translateY(-60%) translateX(0)' : 'translateY(-60%) translateX(-16px)',
                    display: 'flex', flexDirection: 'column', gap: 12, zIndex: 10,
                    opacity: showActions ? 1 : 0,
                    pointerEvents: showActions ? 'auto' : 'none',
                    transition: 'opacity 0.25s ease, transform 0.25s ease',
                  }}>
                    <button type="button" onClick={e => { e.stopPropagation(); handleEditClick(); }} style={actionBtnStyle}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                      Edit
                    </button>
                    <button type="button" onClick={e => { e.stopPropagation(); handleSaveDraft(); }} disabled={isSavingDraft} style={{ ...actionBtnStyle, opacity: isSavingDraft ? 0.7 : 1, cursor: isSavingDraft ? 'not-allowed' : 'pointer' }}>
                      {isSavingDraft
                        ? <span style={{ width: 16, height: 16, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'p4spin 0.8s linear infinite' }} />
                        : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>}
                      {isSavingDraft ? 'Saving…' : 'Draft'}
                    </button>
                  </div>
                )}
                <PhoneMockup
                  card={card} scale={scale} isSelected={isSelected && isC}
                  loading={loading} heroUrl={heroUrl} images={heroImages}
                  businessName={businessName} businessLogo={businessLogo}
                  phoneWidth={phoneWidth}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Dot indicators */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 28 }}>
        {[0, 1, 2].map(i => (
          <div key={i} onClick={() => { setCenterIdx(i); if (!loading && aiCards[i] && !isPointsCard(aiCards[i])) setSelected(aiCards[i].id); setShowActions(false); }}
            style={{ width: i === centerIdx ? 22 : 8, height: 8, borderRadius: 999, background: i === centerIdx ? RED : '#ddd', cursor: 'pointer', transition: 'all 0.3s ease' }} />
        ))}
      </div>

      {/* ── Nav buttons ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: `0 ${pagePaddingH}px`, gap: 12, flexWrap: 'wrap' }}>
        <button type="button" onClick={onBack} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: isMobile ? '9px 16px' : '10px 20px', borderRadius: 8, border: `1.5px solid ${RED}`, background: '#fff', color: RED, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          <ChevronLeft size={15} /> Back
        </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" onClick={handleSaveDraft} disabled={!selectedCard || loading || isSavingDraft} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: isMobile ? '9px 16px' : '12px 24px', borderRadius: 8, border: '1.5px solid #9ca3af', background: '#fff', color: (!selectedCard || loading || isSavingDraft) ? '#9ca3af' : '#374151', fontSize: 14, fontWeight: 600, cursor: (!selectedCard || loading || isSavingDraft) ? 'not-allowed' : 'pointer', opacity: isSavingDraft ? 0.7 : 1 }}>
            {isSavingDraft
              ? <><span style={{ width: 14, height: 14, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'p4spin 0.8s linear infinite' }} /> Saving…</>
              : 'Save Draft'}
          </button>
          <button type="button" onClick={handleNext} disabled={!canPublish || loading} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: isMobile ? '9px 20px' : '12px 28px', borderRadius: 8, border: 'none', background: (!canPublish || loading) ? '#e5e7eb' : RED, color: (!canPublish || loading) ? '#9ca3af' : '#fff', fontSize: 14, fontWeight: 700, cursor: (!canPublish || loading) ? 'not-allowed' : 'pointer' }}>
            Publish <Check size={16} />
          </button>
        </div>
      </div>

      {/* ── Confirm Publication Modal ── */}
      {showConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? 14 : 24 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: isMobile ? '24px 18px 20px' : '40px 40px 32px', maxWidth: 640, width: '100%', boxShadow: '0 24px 80px rgba(0,0,0,0.25)', position: 'relative' }}>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 20 : 40, alignItems: isMobile ? 'flex-start' : 'center', marginBottom: 28 }}>
              {!isMobile && (
                <div style={{ flexShrink: 0 }}>
                  <img src={ConfirmPublishPopupIcon} alt="" width={180} height={180} />
                </div>
              )}
              <div style={{ flex: 1 }}>
                <h2 style={{ margin: '0 0 20px', fontSize: isMobile ? 22 : 28, fontWeight: 400, color: '#1a1a1a' }}>
                  Confirm <strong style={{ fontWeight: 800 }}>Publication</strong>
                </h2>
                <div onClick={() => setTermsAccepted(v => !v)} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 20, cursor: 'pointer' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, border: termsAccepted ? 'none' : `2px solid ${RED}`, background: termsAccepted ? '#22c55e' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s, border 0.2s', marginTop: 2 }}>
                    {termsAccepted && <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7l4 4 6-7" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                  </div>
                 <div>
                    <div style={{ fontSize: 15, color: '#1a1a1a', lineHeight: 1.4 }}>
                      Please accept our{' '}
                      <a href="/terms-of-service/business" target="_blank" rel="noopener noreferrer" style={{ color: RED, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }} onClick={e => e.stopPropagation()}>Terms & Conditions</a>
                    </div>
                    <div style={{ fontSize: 12, color: '#999', fontStyle: 'italic', marginTop: 2 }}>(Required to continue)</div>
                  </div>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <button type="button" onClick={() => { setShowConfirm(false); setTermsAccepted(false); setSendPush(false); }} disabled={isPublishing} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 22px', borderRadius: 8, border: `1.5px solid ${RED}`, background: '#fff', color: RED, fontSize: 14, fontWeight: 600, cursor: isPublishing ? 'not-allowed' : 'pointer', opacity: isPublishing ? 0.6 : 1 }}>
                <X size={14} /> Close
              </button>
              <button type="button" onClick={handleConfirmProceed} disabled={!termsAccepted || isPublishing} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 28px', borderRadius: 8, border: 'none', background: termsAccepted ? RED : '#e5e7eb', color: termsAccepted ? '#fff' : '#9ca3af', fontSize: 14, fontWeight: 700, cursor: (termsAccepted && !isPublishing) ? 'pointer' : 'not-allowed', transition: 'background 0.2s' }}>
                {isPublishing
                  ? <><span style={{ width: 14, height: 14, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'p4spin 0.8s linear infinite' }} /> Publishing…</>
                  : <>Publish <Check size={16} /></>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}